import { inject, Injectable } from "@angular/core";
import { LOGGER_URL } from "@shared/providers/logger.provider";

@Injectable({
    providedIn: "root",
})
// Custom LoggerService that sends log messages to a specified server endpoint.
// It provides methods for logging informational and error messages, and ensures that logging does not interfere with the application's flow such as interceptor.
export class LoggerService {
    // The loggerUrl is injected from the LOGGER_URL provider. If not provided, logging to the server will be disabled.
    private readonly loggerUrl: string | null = inject(LOGGER_URL, { optional: true });

    /**
     * Sends an informational log entry to the logging server.
     *
     * @param message Log message content.
     * @param stackTrace Optional error stack trace or debug details.
     */
    public log(message: string, stackTrace?: string): void {
        this.sendLogToServer("info", message, stackTrace);
    }

    /**
     * Sends an error log entry to the logging server.
     *
     * @param message Error description message.
     * @param stackTrace Optional error stack trace or debug details.
     */
    public error(message: string, stackTrace?: string): void {
        this.sendLogToServer("error", message, stackTrace);
    }

    /**
     * Asynchronously dispatches formatted log payload via native Fetch API with authentication header.
     *
     * @param level Severity level (`info` or `error`).
     * @param message Log text content.
     * @param stackTrace Optional error stack trace string.
     */
    private sendLogToServer(level: string, message: string, stackTrace?: string): void {
        if (!this.loggerUrl) {
            console.warn("Logger URL is not provided. Cannot send log to server.");
            return;
        }

        const token: string | null = sessionStorage.getItem("accessToken");

        if (!token) {
            console.warn("No token found in session storage. Cannot send log to server.");
            return;
        }

        // Use the Fetch API to send the log message to the server. The keepalive option allows the request to be sent even when the page is being unloaded.
        fetch(this.loggerUrl, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ level, message, stackTrace, timestamp: new Date().toISOString() }),
            // Keep the connection alive for background logging even when the page is being unloaded
            keepalive: true,
        }).catch((error: Error) => {
            // Logging must never break the app flow.
            console.error("Failed to send log to server:", error);
        });
    }
}
